import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { TriggerData } from "../../models/trigger/TriggerData.model";
import { TriggerManagerAction } from "./trigger-manager.action";
import { finalize, from, map, of, switchMap, throwError } from "rxjs";
import { CompoApp } from "../../services/CompoApp";
import { TriggerCondition } from "../../enums/trigger/TriggerCondition";
import { JobTemplateConditionType } from "../../enums/job_template/JobTemplateConditionType";
import { TriggerType } from "../../enums/trigger/TriggerType";
import { ResetStateDefaults } from "../base-action-class.model";
import { Observable } from "rxjs/internal/Observable";
import { TriggerRepetitionType } from "../../enums/trigger/TriggerRepetitionType";
import { TriggerEndType } from "../../enums/trigger/TriggerEndType";
import { ModuleTranslatedDtoModel } from "../../models/module/module-translated-dto.model";
import UpdateManagedTrigger = TriggerManagerAction.UpdateManagedTrigger;

export enum TriggerManagerMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT'
}

interface TriggerManagerStateModel {
  routineId: string;
  isTemplate: boolean;
  moduleId: string;
  moduleVersion: number;
  triggers: TriggerData[];
  managerEnabled: boolean;
  managerActive: boolean;
  managerMode: TriggerManagerMode;
  managedTriggerId: string;
  managedTrigger: TriggerData;
  updatedManagedTrigger: TriggerData;
  triggerConditions: Set<TriggerCondition | JobTemplateConditionType>;
  selectedCondition: TriggerCondition | JobTemplateConditionType;
  mostRecentDateValue: Date;
  noEndConditionForManagedTrigger: boolean;
  inProgress: boolean;
}

const getDefaults = (): TriggerManagerStateModel => ({
  routineId: null,
  isTemplate: null,
  moduleId: null,
  moduleVersion: null,
  triggers: null,
  managerEnabled: true,
  managerActive: false,
  managerMode: null,
  managedTriggerId: null,
  managedTrigger: null,
  updatedManagedTrigger: null,
  noEndConditionForManagedTrigger: true,
  triggerConditions: new Set<TriggerCondition>([TriggerCondition.AND, TriggerCondition.OR, TriggerCondition.THEN]),
  selectedCondition: TriggerCondition.THEN,
  mostRecentDateValue: null,
  inProgress: false,
});


@State<TriggerManagerStateModel>({
  name: 'triggerManager',
  defaults: getDefaults()
})
@Injectable()
export class TriggerManagerXsState {

  //region CONSTRUCTOR
  constructor(
    private app: CompoApp,
    private store: Store
  ) { }
  //endregion

  //region SELECTORS
  @Selector()
  public static triggers(state: TriggerManagerStateModel): TriggerData[] | null {
    return state.triggers;
  }

  @Selector()
  public static triggerTypes(state: TriggerManagerStateModel): Set<TriggerType> {
    if (!state.triggers) {
      return null;
    }

    const triggerTypes: Set<TriggerType> = new Set<TriggerType>();

    for (const trigger of state.triggers) {
      if (trigger) {
        triggerTypes.add(trigger.triggerType);
      }
    }

    if (state.managerActive && state.managedTrigger?.triggerType) {
      triggerTypes.add(state.managedTrigger.triggerType);
    }

    return triggerTypes;
  }

  @Selector()
  public static createPossible(state: TriggerManagerStateModel): boolean {
    if (!state.managerEnabled) {
      return false;
    }

    if (!state.triggers?.length) {
      return true;
    }

    const triggerTypes: Set<TriggerType> = TriggerManagerXsState.triggerTypes(state);

    return (triggerTypes.size == 1 && triggerTypes.has(TriggerType.DATETIME))
      || state.triggers.length < 2;
  }

  @Selector()
  public static editAllowed(state: TriggerManagerStateModel): boolean {
    return !state.inProgress && !state.managerActive && state.managerEnabled;
  }

  @Selector()
  public static managerActive(state: TriggerManagerStateModel): boolean {
    return state.managerActive;
  }

  @Selector()
  public static mode(state: TriggerManagerStateModel): TriggerManagerMode | null {
    return state.managerMode;
  }

  @Selector()
  public static managedTriggerId(state: TriggerManagerStateModel): string {
    return state.managedTriggerId;
  }

  @Selector()
  public static triggerConditions(state: TriggerManagerStateModel): Set<TriggerCondition | JobTemplateConditionType> {
    return state.triggerConditions;
  }

  @Selector()
  public static selectedCondition(state: TriggerManagerStateModel): JobTemplateConditionType | TriggerCondition {
    return state.selectedCondition;
  }

  @Selector()
  public static mostRecentDateValue(state: TriggerManagerStateModel): Date {
    return state.mostRecentDateValue;
  }

  @Selector()
  public static showDateChangeWarning(state: TriggerManagerStateModel): boolean {
    if (!state.managerActive || state.managerMode === TriggerManagerMode.CREATE) {
      return false;
    }

    return state.managedTrigger?.startDate != state.updatedManagedTrigger?.startDate
      || state.managedTrigger?.endDate != state.updatedManagedTrigger?.endDate
      || state.managedTrigger?.executionAmount != state.updatedManagedTrigger?.executionAmount;
  }

  @Selector()
  public static noEndConditionForManagedTrigger(state: TriggerManagerStateModel): boolean {
    return state.noEndConditionForManagedTrigger;
  }

  @Selector()
  public static allEntriesSaved(state: TriggerManagerStateModel): boolean {
    if (!state.triggers) {
      return true;
    }
    return state.triggers.every((trigger: TriggerData) => trigger != null);
  }

  @Selector()
  public static inProgress(state: TriggerManagerStateModel): boolean {
    return state.inProgress;
  }
  //endregion

  //region ACTIONS
  @Action(TriggerManagerAction.Initialize)
  public initialize(
    ctx: StateContext<TriggerManagerStateModel>,
    action: TriggerManagerAction.Initialize
  ): Observable<void> {
    const state: TriggerManagerStateModel = ctx.getState();

    if(!action.routineId) {
      return throwError(() => new Error('Routine id can not be null!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Trigger request is in progress!'));
    }

    if (action.moduleId && action.moduleVersion === null) {
      return throwError(() => new Error('Module version cannot be null if module id is defined!'));
    }

    ctx.patchState({
      ...getDefaults(),
      routineId: action.routineId,
      isTemplate: action.isTemplate,
      moduleId: action.moduleId,
      moduleVersion: action.moduleVersion,
    });

    let moduleRequest: Observable<any> = of(null);

    if (action.moduleId) {
      moduleRequest = from(this.app.moduleService
        .fetchModuleByIdAndReleaseNumber(action.moduleId, action.moduleVersion));
    }

    return moduleRequest
      .pipe(
        switchMap((module: ModuleTranslatedDtoModel) => {
          const managerEnabled: boolean
            = !module || (module.releaseNumber < action.moduleVersion && !module.isReleasing && !module.isReleased);
          ctx.patchState({managerEnabled});
          return this.store.dispatch(new TriggerManagerAction.FetchTriggers());
        })
      );
  }

  @Action(TriggerManagerAction.SelectCondition)
  public selectCondition(
    ctx: StateContext<TriggerManagerStateModel>,
    action: TriggerManagerAction.SelectCondition
  ): void {
    const state: TriggerManagerStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Trigger request is in progress!');
    }

    ctx.patchState({
      selectedCondition: action.triggerCondition
    });
  }

  @Action(TriggerManagerAction.Create)
  public create(
    ctx: StateContext<TriggerManagerStateModel>
  ): void {
    const state: TriggerManagerStateModel = ctx.getState();

    if (!state.managerEnabled) {
      throw new Error('Cannot create trigger! Manager is not enabled!');
    }

    if (state.managerActive) {
      throw new Error('Cannot create trigger! Manager is already active!');
    }

    if (state.inProgress) {
      throw new Error('Trigger request is in progress!');
    }

    ctx.patchState({
      managerActive: true,
      managerMode: TriggerManagerMode.CREATE,
      triggers: [...state.triggers, null],
      noEndConditionForManagedTrigger: false
    });

    this.setMostRecentDateValueForManagedTrigger(ctx);
  }

  @Action(TriggerManagerAction.TakeOverTriggersFromTemplate)
  public takeOverTriggersFromTemplate(
    ctx: StateContext<TriggerManagerStateModel>
  ): Observable<void> {
    const state: TriggerManagerStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Trigger request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.app.jobroutineTriggerService.takeOverTriggersFromTemplate(state.routineId))
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new TriggerManagerAction.FetchTriggers(true));
        }),
        finalize(() => ctx.patchState({
          inProgress: false
        })),
      );
  }

  @Action(TriggerManagerAction.Edit)
  public edit(
    ctx: StateContext<TriggerManagerStateModel>,
    action: TriggerManagerAction.Edit
  ): void {
    const state: TriggerManagerStateModel = ctx.getState();

    if (action.triggerId == null) {
      throw new Error('Trigger id cannot be null!');
    }

    if (!state.managerEnabled) {
      throw new Error('Cannot edit trigger! Manager is not enabled!');
    }

    if (state.managerActive) {
      throw new Error('Cannot edit trigger! Manager is already active!');
    }

    if (state.inProgress) {
      throw new Error('Trigger request is in progress!');
    }

    const managedTrigger: TriggerData = state.triggers?.find(trigger => trigger.id === action.triggerId);

    if (!managedTrigger) {
      throw new Error('Trigger cannot be found!');
    }

    ctx.patchState({
      managerActive: true,
      managerMode: TriggerManagerMode.EDIT,
      managedTriggerId: action.triggerId,
      managedTrigger,
      updatedManagedTrigger: {...managedTrigger}
    });

    this.setMostRecentDateValueForManagedTrigger(ctx);
  }

  @Action(TriggerManagerAction.UpdateManagedTrigger)
  public updateManagedTrigger(
    ctx: StateContext<TriggerManagerStateModel>,
    action: UpdateManagedTrigger
  ): void {
    const state: TriggerManagerStateModel = ctx.getState();

    if (!state.managerActive) {
      throw new Error('No active manager! Update can not be performed!');
    }

    const updateConditions: boolean = state?.managedTrigger?.triggerType !== action?.triggerData.triggerType;
    let noEndCondition: boolean;
    if (action.triggerData) {
      noEndCondition = action.triggerData.repetitionType !== TriggerRepetitionType.NO_REPETITION
        && action.triggerData.endType === TriggerEndType.NO_END
        && state.selectedCondition === TriggerCondition.THEN;
    } else {
      noEndCondition = false;
    }

    ctx.patchState({
      updatedManagedTrigger: action.triggerData,
      noEndConditionForManagedTrigger: noEndCondition
    });

    if (updateConditions) {
      this.setPossibleConditions(ctx);
    }
  }

  @Action(TriggerManagerAction.Save)
  public save(
      ctx: StateContext<TriggerManagerStateModel>
  ): Observable<void> {
    const state: TriggerManagerStateModel = ctx.getState();

    if (!state.managerActive) {
      return throwError(() => new Error('No active manager! Save can not be performed!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Trigger request is in progress!'));
    }

    let request: Promise<any>;

    switch (state.managerMode) {
      case TriggerManagerMode.CREATE: {
        request = this.app.jobroutineTriggerService.createTrigger(
          state.routineId,
          {
            ...state.updatedManagedTrigger,
            id: null
          },
          state.selectedCondition,
          state.isTemplate,
          state.moduleId,
          state.moduleVersion
        );
        break;
      }
      case TriggerManagerMode.EDIT: {
        request = this.app.jobroutineTriggerService.updateTrigger(
          state.routineId,
          {
            ...state.updatedManagedTrigger,
            id: state.managedTriggerId
          },
          state.selectedCondition,
          state.isTemplate,
          state.moduleId,
          state.moduleVersion
        );
        break;
      }
    }

    ctx.patchState({
      inProgress: true
    });

    return from(request)
        .pipe(
          switchMap(() => {
              ctx.patchState({
                managerActive: false,
                managerMode: null,
                managedTriggerId: null,
                managedTrigger: null,
                updatedManagedTrigger: null,
                mostRecentDateValue: null
              });
              return this.store.dispatch(new TriggerManagerAction.FetchTriggers(true));
            }),
          finalize(() => ctx.patchState({
            inProgress: false
          })),
        );
  }

  @Action(TriggerManagerAction.Delete)
  public delete(
    ctx: StateContext<TriggerManagerStateModel>,
    action: TriggerManagerAction.Delete
  ): Observable<void> {
    const state: TriggerManagerStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Trigger request is in progress!'));
    }

    const triggerId = action.triggerId;

    if (triggerId == null) {
      ctx.patchState({
        managerActive: false,
        managerMode: null,
        managedTriggerId: null,
        managedTrigger: null,
        updatedManagedTrigger: null,
        triggers: [...state.triggers].filter((trigger: TriggerData) => !!trigger),
        mostRecentDateValue: null,
        inProgress: false,
      });
      return;
    } else {

      ctx.patchState({
        inProgress: true
      });

      return from(this.app.jobroutineTriggerService.deleteTrigger(
        state.routineId,
        triggerId,
        state.isTemplate,
        state.moduleId,
        state.moduleVersion))
        .pipe(
          switchMap(() => {
            ctx.patchState({
              managerActive: false,
              managerMode: null,
              managedTriggerId: null,
              managedTrigger: null,
              updatedManagedTrigger: null,
              mostRecentDateValue: null,
            });
            return this.store.dispatch(new TriggerManagerAction.FetchTriggers(true));
          }),
          finalize(() => ctx.patchState({inProgress: false})),
        );
    }
  }

  @Action(TriggerManagerAction.CloseManager)
  public closeEditMode(
    ctx: StateContext<TriggerManagerStateModel>,
  ): void {
    const state: TriggerManagerStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Trigger request is in progress!');
    }

    ctx.patchState({
      managerActive: false,
      managerMode: null,
      managedTriggerId: null,
      managedTrigger: null,
      updatedManagedTrigger: null,
      triggers: [...state.triggers].filter((trigger: TriggerData) => !!trigger),
      mostRecentDateValue: null,
      inProgress: false,
    });

  }

  @Action(TriggerManagerAction.FetchTriggers)
  public fetchTriggers(
    ctx: StateContext<TriggerManagerStateModel>,
    action: TriggerManagerAction.FetchTriggers
  ): Observable<void> {
    const state: TriggerManagerStateModel = ctx.getState();

    if(!state.routineId) {
      return throwError(() => new Error('Job routine id can not be null!'));
    }

    if (state.managerActive && !action.afterSave) {
      return throwError(() => new Error('Cannot edit trigger! Manager is already active!'));
    }

    if (state.inProgress && !action.afterSave) {
      return throwError(() => new Error('Trigger request is in progress!'));
    }
    return from(this.app.jobroutineTriggerService.getAllByRoutineId(
      state.routineId,
      state.isTemplate,
      state.moduleId,
      state.moduleVersion)
    )
      .pipe(
        map((triggers: TriggerData[]) => {
          ctx.patchState({triggers});
          this.setPossibleConditions(ctx);
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action([
    TriggerManagerAction.ResetExceptTriggers,
    ResetStateDefaults
  ])
  public resetExceptTriggers(
      ctx: StateContext<TriggerManagerStateModel>
  ): void {
    const state: TriggerManagerStateModel = ctx.getState();
    const triggers = state.triggers;
    ctx.setState({
      ...getDefaults(),
      triggers
    });
  }
  //endregion

  //region METHODS
  private setPossibleConditions(ctx: StateContext<TriggerManagerStateModel>): void {
    const state: TriggerManagerStateModel = ctx.getState();

    const possibleTriggerConditions: Set<TriggerCondition | JobTemplateConditionType>
      = this.getPossibleTriggerConditions(TriggerManagerXsState.triggerTypes(state));
    const selectedCondition: TriggerCondition | JobTemplateConditionType
      = possibleTriggerConditions.has(state.selectedCondition)
      ? state.selectedCondition : [...possibleTriggerConditions][0];

    ctx.patchState({
      triggerConditions: possibleTriggerConditions,
      selectedCondition: selectedCondition
    });
  }

  private getPossibleTriggerConditions(triggerTypes: Set<TriggerType>): Set<TriggerCondition | JobTemplateConditionType> {
    if (triggerTypes) {
      if (triggerTypes.has(TriggerType.DATETIME)
        && triggerTypes.has(TriggerType.ATTRIBUTE)) {
        return new Set<TriggerCondition>([TriggerCondition.OR, TriggerCondition.THEN]);
      }
      if (triggerTypes.size === 1) {
        if (triggerTypes.has(TriggerType.DATETIME)) {
          return new Set<TriggerCondition>([TriggerCondition.THEN]);
        }
      }
    }

    return new Set<TriggerCondition>([TriggerCondition.AND, TriggerCondition.OR, TriggerCondition.THEN]);
  }

  private setMostRecentDateValueForManagedTrigger(ctx: StateContext<TriggerManagerStateModel>): void {
    const state: TriggerManagerStateModel = ctx.getState();

    let managedTriggerIndex: number;

    if (state.managerMode === TriggerManagerMode.CREATE) {
      managedTriggerIndex = state.triggers.length - 1;
    } else {
      managedTriggerIndex = state.triggers.findIndex((trigger: TriggerData) => trigger.id === state.managedTriggerId);
    }

    if (managedTriggerIndex > 0) {
      let mostRecentDateValue: number = null;

      for (let i = managedTriggerIndex - 1; i >= 0 ; i--) {
        const previousTrigger: TriggerData = state.triggers[i];

        if (previousTrigger.lastPlannedExecutionOn) {
          mostRecentDateValue = previousTrigger.lastPlannedExecutionOn;
        } else if (previousTrigger.startDate) {
          mostRecentDateValue = previousTrigger.startDate;
        }

        if (mostRecentDateValue) {
          break;
        }
      }

      ctx.patchState({
        mostRecentDateValue: mostRecentDateValue ? new Date(mostRecentDateValue) : null
      });
    } else {
      ctx.patchState({
        mostRecentDateValue: null
      });
    }
  }
  //endregion
}
